import { useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, Paper, Stack, TextField, Typography,
} from '@mui/material';
import { parseInviteeImport } from '../../../lib/csv';
import InviteeImportPlanPanel from './InviteeImportPlanPanel';
import { buildImportRows, defaultDecisions } from '../../../lib/inviteeImport';
import type {
  ImportDecisions,
  InviteeImportPreview,
  InviteeImportResult,
} from '../../../lib/inviteeImport';
import { summarizeReachability } from '../../../lib/invitee-send';
import type { InviteReachability } from '../../../lib/invitee-send';
import type {
  GroupDetail,
  GroupInviteeInput,
  SendGroupInvitesRequest,
  SendGroupInvitesResponse,
} from '../../../types/groups';
import { plural } from '../../../lib/plural';

export default function RosterConsole({
  group,
  onPreviewImport,
  onImport,
  onSend,
  saving,
  sending,
  error,
  sendError,
  sendResult,
}: {
  group: GroupDetail;
  onPreviewImport: (invitees: GroupInviteeInput[]) => Promise<InviteeImportPreview>;
  onImport: (
    invitees: GroupInviteeInput[],
    rosterFingerprint: string,
  ) => Promise<InviteeImportResult>;
  onSend: (body: SendGroupInvitesRequest) => Promise<SendGroupInvitesResponse>;
  saving: boolean;
  sending: boolean;
  error: string;
  sendError: string;
  sendResult?: SendGroupInvitesResponse;
}) {
  const [bulkText, setBulkText] = useState('');
  const [parseError, setParseError] = useState('');
  const [reviewing, setReviewing] = useState(false);
  // The rows the preview was computed from, kept so `rowIndex` still indexes
  // into them when the host confirms. Re-parsing at confirm time would risk
  // building a different list than the one the server planned against.
  const [parsedRows, setParsedRows] = useState<GroupInviteeInput[]>([]);
  const [preview, setPreview] = useState<InviteeImportPreview | null>(null);
  const [decisions, setDecisions] = useState<ImportDecisions>({});
  /*
    What the write actually did, kept on screen after the plan is cleared.

    The import survives a bad row rather than failing the batch — a duplicate
    address, or two rows claiming one guest — and reports those rows instead of
    writing them. That report used to be discarded at the call site, so a host
    pasting 137 rows could have rows silently not land and be shown a cleared
    paste box, which reads as "all done". A partial success that reports nothing
    is worse than an error.
  */
  const [importResult, setImportResult] = useState<InviteeImportResult | null>(null);
  const [messageTemplate, setMessageTemplate] = useState('Hey {name} - you are invited to {title}. View trip details and room options here: {url}');
  // Who the email signs as. Blank falls back to the hosts' display names.
  const [signedBy, setSignedBy] = useState('');
  const [confirmSend, setConfirmSend] = useState(false);
  const counts = group.invitees.reduce<Record<string, number>>((acc, invitee) => {
    acc[invitee.rsvpStatus] = (acc[invitee.rsvpStatus] || 0) + 1;
    return acc;
  }, {});
  const reach = summarizeReachability(group.invitees);

  const resetPlan = () => {
    setPreview(null);
    setParsedRows([]);
    setDecisions({});
  };

  const handleReview = async () => {
    setParseError('');
    let parsed: GroupInviteeInput[];
    try {
      parsed = parseInviteeImport(bulkText);
    } catch (err: any) {
      resetPlan();
      setParseError(err.message || 'Could not parse invitees.');
      return;
    }
    if (!parsed.length) {
      setParseError('Paste at least one invitee row.');
      return;
    }

    setReviewing(true);
    try {
      // The server matches; Studio only renders the answer. Asking it is what
      // makes the plan on screen the plan that runs.
      const plan = await onPreviewImport(parsed);
      setParsedRows(parsed);
      setPreview(plan);
      setDecisions(defaultDecisions(plan));
    } catch (err: any) {
      resetPlan();
      setParseError(err?.response?.data?.error || err?.message || 'Could not check this import.');
    } finally {
      setReviewing(false);
    }
  };

  const handleImport = async () => {
    if (!preview) return;
    try {
      const result = await onImport(
        buildImportRows(parsedRows, preview, decisions),
        preview.rosterFingerprint,
      );
      setImportResult(result);
      // The paste is only cleared once its outcome is on screen. Clearing it
      // while the outcome was thrown away is what made a partial import look
      // like a complete one.
      setBulkText('');
      resetPlan();
    } catch {
      // react-query exposes the failure through `error`; keep the paste and the
      // plan on screen so a retry doesn't cost the host their clipboard. A 409
      // means the roster moved — the host re-runs Review to get fresh ids.
    }
  };

  const handleSend = async (dryRun: boolean) => {
    setConfirmSend(false);
    try {
      await onSend({
        dryRun,
        messageTemplate,
        signedBy: signedBy.trim() || undefined,
      });
    } catch {
      // react-query exposes the error through sendError.
    }
  };

  // Any edit to the paste invalidates the plan it was computed from — and with
  // it every invitee id the host approved.
  const handleBulkTextChange = (value: string) => {
    setBulkText(value);
    if (preview) resetPlan();
    // The last import's outcome describes rows that are no longer on screen.
    if (importResult) setImportResult(null);
  };

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 3, borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={800}>Import invitees</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
          Paste CSV from Partiful/export tools. Header columns can be name, email, phone, rsvp_status, notes, segment.
          Without headers, Studio reads columns as name, email, phone, status, notes, segment.
          Segment values: core, travel_party (blank = all-guest broadcasts only).
          To add missing emails and phones to guests already here, paste name + email + phone and leave
          the RSVP column out — Review shows you which existing guest each row attaches to before anything is written.
        </Typography>
        {(parseError || error) ? <Alert severity="error" sx={{ mb: 2 }}>{parseError || error}</Alert> : null}
        {importResult ? <ImportOutcome result={importResult} onDismiss={() => setImportResult(null)} /> : null}
        <TextField
          fullWidth
          multiline
          minRows={5}
          value={bulkText}
          onChange={(e) => handleBulkTextChange(e.target.value)}
          placeholder={'name,email,phone,rsvp_status,notes\nSarah Mitchell,sarah@example.com,+14155550142,going,Vegetarian'}
        />
        {!preview ? (
          <Button
            sx={{ mt: 2 }}
            variant="contained"
            disabled={saving || reviewing || !bulkText.trim()}
            onClick={handleReview}
          >
            {reviewing ? 'Checking...' : 'Review import'}
          </Button>
        ) : (
          <InviteeImportPlanPanel
            preview={preview}
            decisions={decisions}
            onDecisions={setDecisions}
            saving={saving}
            onCancel={resetPlan}
            onConfirm={handleImport}
          />
        )}
      </Paper>
      <Paper sx={{ p: 3, borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={800}>Send invites</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
          Sends to every imported invitee with an email or phone using the shared Tripsby notification workflow.
          Variables: {'{name}'}, {'{title}'}, {'{location}'}, {'{url}'}.
        </Typography>
        {!group.published ? (
          <Alert severity="warning" sx={{ mb: 2 }}>Publish this group before sending invites.</Alert>
        ) : null}
        {reach.total > 0 && reach.reachable < reach.total ? (
          <Alert severity={reach.reachable === 0 ? 'error' : 'warning'} sx={{ mb: 2 }}>
            <ReachabilityBreakdown reach={reach} />
          </Alert>
        ) : null}
        {sendError ? <Alert severity="error" sx={{ mb: 2 }}>{sendError}</Alert> : null}
        <TextField
          fullWidth
          multiline
          minRows={3}
          label="Invite message"
          value={messageTemplate}
          onChange={(e) => setMessageTemplate(e.target.value)}
        />
        <TextField
          fullWidth
          label="Signed by"
          placeholder="e.g. Michael — blank signs as the trip's hosts"
          value={signedBy}
          onChange={(e) => setSignedBy(e.target.value)}
          error={signedBy.includes('@')}
          helperText={signedBy.includes('@') ? 'Sign with a name, not an email address.' : undefined}
          sx={{ mt: 2 }}
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            disabled={sending || !group.invitees.length}
            onClick={() => handleSend(true)}
          >
            Preview messages
          </Button>
          <Button
            variant="contained"
            disabled={sending || !group.published || reach.reachable === 0}
            onClick={() => setConfirmSend(true)}
          >
            {sending ? 'Sending...' : `Send ${reach.reachable} invites`}
          </Button>
        </Stack>
        {sendResult ? (
          <Box sx={{ mt: 2 }}>
            <Alert severity={sendResult.sentCount > 0 ? 'success' : 'info'} sx={{ mb: 1.5 }}>
              {sendResult.sentCount > 0
                ? `Sent ${sendResult.sentCount}; skipped ${sendResult.skippedCount}.`
                : `Previewed ${plural(sendResult.results.length, 'invite message')}.`}
            </Alert>
            <Stack spacing={1}>
              {sendResult.results.slice(0, 5).map((result) => (
                <Paper key={result.inviteeId} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                    <Typography variant="body2" fontWeight={700}>{result.fullName}</Typography>
                    <Chip size="small" label={result.status} color={result.status === 'sent' ? 'success' : result.status === 'failed' ? 'error' : 'default'} />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{result.message}</Typography>
                  {result.error ? <Typography variant="caption" color="error">{result.error}</Typography> : null}
                </Paper>
              ))}
            </Stack>
          </Box>
        ) : null}
      </Paper>

      <Dialog open={confirmSend} onClose={() => setConfirmSend(false)}>
        <DialogTitle>Send {reach.reachable} invites?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <ReachabilityBreakdown reach={reach} />
            <Box sx={{ mt: 1.5 }}>
              Unsubscribed guests are skipped too, so the real number may be lower.
              This sends for real — there is no undo.
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSend(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => handleSend(false)}>
            Send {reach.reachable} now
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

/**
 * What the import did, including what it refused.
 *
 * Severity is driven by whether anything was skipped, not by whether the request
 * succeeded: the API answers 200 with per-row errors on purpose, so that one bad
 * row costs one row instead of all 137. A green "done" over a body that says four
 * rows were skipped is how a host walks away from a roster that is four guests
 * short — and finds out when four people never get a message.
 */
function ImportOutcome({
  result,
  onDismiss,
}: {
  result: InviteeImportResult;
  onDismiss: () => void;
}) {
  const skipped = result.skippedCount > 0;
  return (
    <Alert severity={skipped ? 'warning' : 'success'} sx={{ mb: 2 }} onClose={onDismiss}>
      <Box>
        {plural(result.updatedCount, 'guest')} updated, {result.createdCount} added
        {skipped ? `, ${result.skippedCount} not written` : ''}.
      </Box>
      {result.errors.length > 0 ? (
        <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
          {result.errors.map((rowError) => (
            <li key={rowError.row}>
              <Typography variant="caption">
                Row {rowError.row} ({rowError.fullName || 'no name'}): {rowError.error}
              </Typography>
            </li>
          ))}
        </Box>
      ) : null}
    </Alert>
  );
}

function ReachabilityBreakdown({ reach }: { reach: InviteReachability }) {
  return (
    <>
      <Box>
        <strong>{reach.reachable}</strong> of {reach.total} guests will actually be messaged.
      </Box>
      {reach.noContact > 0 ? (
        <Box>{reach.noContact} have no email or phone on file and will be skipped.</Box>
      ) : null}
      {reach.declined > 0 ? (
        <Box>{reach.declined} declined and will be skipped.</Box>
      ) : null}
    </>
  );
}
